import React, { useCallback } from 'react';
import {
  DefaultValues,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { RuleDto } from '@area-restrita/api-client/model/ruleDto';
import { UpdateDepartmentDto } from '@area-restrita/api-client/model/updateDepartmentDto';

import { Form, FormField } from '@/components/ui/form';
import { CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import InlineInput from '@/components/InlineInput';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';

import { companyPermissionCategories } from './permissions';

const schema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  description: z.string().optional(),
  rules: z.array(
    z.object({
      action: z.string().min(1, 'Ação é obrigatória'),
      subject: z.string().min(1, 'Assunto é obrigatório'),
    }),
  ),
});

interface IProps {
  defaultValues?: DefaultValues<UpdateDepartmentDto>;
  onSubmit: SubmitHandler<UpdateDepartmentDto>;
}

const DepartmentForm: React.FC<IProps> = ({ defaultValues, onSubmit }) => {
  const form = useForm<UpdateDepartmentDto>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const rules = useFieldArray({
    control: form.control,
    name: 'rules',
  });

  const isPermissionSelected = useCallback(
    (rule: RuleDto) =>
      rules.fields.some(
        (r) => r.action === rule.action && r.subject === rule.subject,
      ),
    [rules.fields],
  );

  const handlePermissionToggle = useCallback(
    (rule: RuleDto, checked: boolean) => {
      if (checked) {
        rules.append({ action: rule.action, subject: rule.subject });
        return;
      }

      rules.remove(
        rules.fields.findIndex(
          (r) => r.action === rule.action && r.subject === rule.subject,
        ),
      );
    },
    [rules],
  );

  return (
    <Form {...form}>
      <CardContent className="flex flex-col gap-2">
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <InlineInput
              title="Nome"
              placeholder="Nome da função"
              disabled={isSubmitting}
              {...field}
            />
          )}
        />
        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <InlineInput
              title="Descrição"
              placeholder="Descrição da função"
              as={Textarea}
              disabled={isSubmitting}
              {...field}
            />
          )}
        />

        <div>
          <h4 className="font-semibold text-xl mb-4">Permissões</h4>
          <Tabs
            defaultValue={companyPermissionCategories.at(0)?.category}
            className="w-full"
          >
            <TabsList className="w-full justify-start">
              {companyPermissionCategories.map(({ category }) => (
                <TabsTrigger key={category} value={category}>
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
            {companyPermissionCategories.map(({ category, subCategories }) => (
              <TabsContent
                key={category}
                value={category}
                className="space-y-6 mt-3"
              >
                {subCategories.map(({ rules, subCategory }) => (
                  <div key={subCategory} className="border rounded-lg p-4">
                    <h5 className="font-medium mb-3 text-sm text-gray-700">
                      {subCategory}
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {rules.map((rule) => (
                        <div
                          key={`${rule.action}-${rule.subject}`}
                          className="flex items-start gap-3"
                        >
                          <Checkbox
                            id={`permission-${rule.action}-${rule.subject}`}
                            checked={isPermissionSelected(rule)}
                            onCheckedChange={(checked) =>
                              handlePermissionToggle(rule, checked as boolean)
                            }
                          />
                          <label
                            className="flex-1 text-sm font-medium cursor-pointer"
                            htmlFor={`permission-${rule.action}-${rule.subject}`}
                          >
                            {rule.label}
                            <p className="text-xs font-normal text-muted-foreground mt-1">
                              {rule.description}
                            </p>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button
          type="submit"
          disabled={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          Salvar Alterações
        </Button>
      </CardFooter>
    </Form>
  );
};

export default DepartmentForm;
